import { useEffect, useMemo, useRef } from "react";
import { Input } from "antd";
import { maskCep } from "../../../utils/mask";
import { maskMoney } from "../../../utils/mask";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { usePropostaForm } from "../context/PropostaFormContext";
import { usePropostaImovel } from "../hooks/usePropostaImovel";
import { formatBuildingLabel } from "../utils/formatBuildingLabel";
import PropostaSectionCard from "../components/PropostaSectionCard";
import SearchableList from "../components/SearchableList";
import PropostaSelect from "../components/PropostaSelect";

export default function PropostaImovelSection() {
  const {
    proposta,
    setProposta,
    mode,
    isReadOnly,
    lockImovelEdit,
    isDark,
  } = usePropostaForm();

  // Cadastro/troca do imóvel só em proposta nova (igual App HUB).
  const canEditImovelCadastro = mode === "create";

  const imovelHook = usePropostaImovel(proposta);
  const {
    buildImovelToSave,
    idImovel,
    descricaoImovel,
    idConstrutora,
    descricaoConstrutora,
    unidade,
    bloco,
    valorProposta,
    cep,
    logradouro,
    numero,
    bairro,
    cidade,
    estado,
    uf,
    isSemiNovo,
    idImovelKurole,
  } = imovelHook;
  const buildingSearchRef = useRef<HTMLDivElement>(null);

  useClickOutside(
    buildingSearchRef,
    () => imovelHook.setShowBuildingList(false),
    imovelHook.showBuildingList,
  );

  useEffect(() => {
    const imovel = buildImovelToSave();
    setProposta((prev) => {
      const prevImovel = prev.imovel;
      if (
        prevImovel?.id_imovel === imovel.id_imovel &&
        prevImovel?.descricao_imovel === imovel.descricao_imovel &&
        prevImovel?.id_construtora === imovel.id_construtora &&
        prevImovel?.descricao_construtora === imovel.descricao_construtora &&
        prevImovel?.unidade === imovel.unidade &&
        prevImovel?.bloco === imovel.bloco &&
        prevImovel?.valor === imovel.valor &&
        prevImovel?.imovel_seminovo === imovel.imovel_seminovo &&
        prevImovel?.id_imovel_kurole === imovel.id_imovel_kurole &&
        prevImovel?.endereco?.cep === imovel.endereco?.cep &&
        prevImovel?.endereco?.logradouro === imovel.endereco?.logradouro &&
        prevImovel?.endereco?.numero === imovel.endereco?.numero &&
        prevImovel?.endereco?.bairro === imovel.endereco?.bairro &&
        prevImovel?.endereco?.cidade === imovel.endereco?.cidade &&
        prevImovel?.endereco?.estado === imovel.endereco?.estado &&
        prevImovel?.endereco?.uf === imovel.endereco?.uf
      ) {
        return prev;
      }
      return { ...prev, imovel };
    });
  }, [
    idImovel,
    descricaoImovel,
    idConstrutora,
    descricaoConstrutora,
    unidade,
    bloco,
    valorProposta,
    cep,
    logradouro,
    numero,
    bairro,
    cidade,
    estado,
    uf,
    isSemiNovo,
    idImovelKurole,
    buildImovelToSave,
    setProposta,
  ]);

  const constructorOptions = imovelHook.constructors.map((c) => ({
    value: String(c.id),
    label: c.nome,
  }));

  const variationPercent = useMemo(() => {
    const rounded = Math.round(imovelHook.variationInfo.percent * 10) / 10;
    return isNaN(rounded) ? undefined : `${rounded}%`;
  }, [imovelHook.variationInfo.percent]);

  const variationColor =
    imovelHook.variationInfo.diffValue > 0
      ? "text-green-600"
      : imovelHook.variationInfo.diffValue < 0
        ? "text-red-500"
        : "text-primary";

  const formatBRL = (value: number) =>
    value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const valorImovelDisplay =
    imovelHook.valorImovel === ""
      ? "R$ 0,00"
      : formatBRL(Number(imovelHook.valorImovel));

  return (
    <div className="space-y-4 min-w-0 max-w-full w-full overflow-x-hidden">
      <PropostaSectionCard title="Imóvel">
        <div ref={buildingSearchRef} className="space-y-3 min-w-0 max-w-full w-full overflow-x-hidden">
          <Field label="">
            <Input
              placeholder="Selecione um Imóvel..."
              disabled={isReadOnly || !canEditImovelCadastro}
              value={imovelHook.searchQuery}
              className="w-full max-w-full"
              onChange={(e) => {
                imovelHook.setSearchQuery(e.target.value);
                imovelHook.setShowBuildingList(true);
              }}
              onFocus={() => {
                if (canEditImovelCadastro) imovelHook.setShowBuildingList(true);
              }}
            />
          </Field>

          {canEditImovelCadastro &&
            !isReadOnly &&
            imovelHook.showBuildingList && (
            <SearchableList
              items={imovelHook.buildings}
              loading={imovelHook.loading}
              getKey={(b) => b.id}
              renderItem={(building) =>
                building.id === -1 ? (
                  <button
                    type="button"
                    className="w-full max-w-full min-w-0 text-left px-3 py-2 text-sm hover:bg-primary/10 text-primary font-medium"
                    onClick={() => imovelHook.selectBuilding(building)}
                  >
                    <span className="block truncate">
                      + Adicionar &quot;{imovelHook.searchQuery}&quot;
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`w-full max-w-full min-w-0 text-left px-3 py-2 text-sm ${
                      isDark ? "hover:bg-gray-800" : "hover:bg-gray-50"
                    }`}
                    onClick={() => imovelHook.selectBuilding(building)}
                  >
                    <span className="block truncate" title={formatBuildingLabel(building)}>
                      {formatBuildingLabel(building)}
                    </span>
                  </button>
                )
              }
            />
          )}

          {imovelHook.idImovel && (
            <p
              className={`text-xs min-w-0 [overflow-wrap:anywhere] ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Imóvel selecionado:{" "}
              <span className="font-semibold text-primary">
                {imovelHook.descricaoImovel || `#${imovelHook.idImovel}`}
              </span>
            </p>
          )}
        </div>
      </PropostaSectionCard>

      <PropostaSectionCard title="Dados do Imóvel">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Construtora *">
            <PropostaSelect
              className="w-full"
              showSearch
              optionFilterProp="label"
              placeholder="Selecione"
              disabled={isReadOnly || lockImovelEdit}
              value={imovelHook.idConstrutora || undefined}
              onChange={(value) => {
                imovelHook.setIdConstrutora(value);
                const c = imovelHook.constructors.find(
                  (item) => String(item.id) === value,
                );
                if (c) imovelHook.setDescricaoConstrutora(c.nome);
              }}
              options={constructorOptions}
            />
          </Field>
          <Field label="Bloco *">
            <Input
              disabled={isReadOnly || lockImovelEdit}
              value={imovelHook.bloco}
              onChange={(e) => imovelHook.setBloco(e.target.value)}
            />
          </Field>
          <Field label="Unidade *">
            <Input
              disabled={isReadOnly || lockImovelEdit}
              value={imovelHook.unidade}
              onChange={(e) => imovelHook.setUnidade(e.target.value)}
            />
          </Field>
          <Field label="Valor da Proposta *">
            <Input
              disabled={isReadOnly}
              value={imovelHook.valorProposta}
              onChange={(e) =>
                imovelHook.setValorProposta(maskMoney(e.target.value))
              }
            />
          </Field>
          <Field label="Imóvel seminovo">
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 14,
                cursor:
                  isReadOnly || lockImovelEdit ? "default" : "pointer",
              }}
            >
              <input
                type="checkbox"
                disabled={isReadOnly || lockImovelEdit}
                checked={Boolean(imovelHook.isSemiNovo)}
                onChange={(e) =>
                  imovelHook.setIsSemiNovo(e.target.checked ? 1 : 0)
                }
              />
              Seminovo
            </label>
          </Field>
        </div>
      </PropostaSectionCard>

      {imovelHook.mostrarEndereco && (
        <PropostaSectionCard title="Endereço do Imóvel">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="CEP *">
              <Input
                disabled={isReadOnly}
                value={imovelHook.cep}
                onChange={(e) => imovelHook.setCep(maskCep(e.target.value))}
              />
            </Field>
            <Field label="Logradouro *">
              <Input
                disabled={isReadOnly}
                value={imovelHook.logradouro}
                onChange={(e) => imovelHook.setLogradouro(e.target.value)}
              />
            </Field>
            <Field label="Número *">
              <Input
                disabled={isReadOnly}
                value={imovelHook.numero}
                onChange={(e) => imovelHook.setNumero(e.target.value)}
              />
            </Field>
            <Field label="Bairro *">
              <Input
                disabled={isReadOnly}
                value={imovelHook.bairro}
                onChange={(e) => imovelHook.setBairro(e.target.value)}
              />
            </Field>
            <Field label="Cidade *">
              <Input
                disabled={isReadOnly}
                value={imovelHook.cidade}
                onChange={(e) => imovelHook.setCidade(e.target.value)}
              />
            </Field>
            <Field label="Estado *">
              <Input
                disabled={isReadOnly}
                value={imovelHook.estado}
                onChange={(e) => imovelHook.setEstado(e.target.value)}
              />
            </Field>
          </div>
        </PropostaSectionCard>
      )}

      <PropostaSectionCard title="Resumo da Proposta">
        <div className="flex flex-col items-end gap-2">
          <DetailRow label="Valor do Imóvel" value={valorImovelDisplay} />
          <DetailRow
            label="Valor da Proposta"
            value={imovelHook.valorProposta || "R$ 0,00"}
          />
          <DetailRow
            label="Variação"
            value={formatBRL(imovelHook.variationInfo.diffValue)}
            extra={variationPercent}
            valueClassName={variationColor}
          />
        </div>
      </PropostaSectionCard>
    </div>
  );
}

function DetailRow({
  label,
  value,
  extra,
  valueClassName = "text-primary",
}: {
  label: string;
  value: string;
  extra?: string;
  valueClassName?: string;
}) {
  const { isDark } = usePropostaForm();

  return (
    <div className="flex items-baseline gap-3 text-sm w-full max-w-sm justify-end">
      <span
        className={`font-medium shrink-0 ${
          isDark ? "text-gray-300" : "text-gray-600"
        }`}
      >
        {label}:
      </span>
      <span className={`font-semibold text-right ${valueClassName}`}>
        {value}
        {extra ? (
          <span className="ml-1 text-xs font-medium opacity-80">({extra})</span>
        ) : null}
      </span>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const { isDark } = usePropostaForm();

  return (
    <div className="min-w-0 max-w-full w-full">
      {label ? (
        <label
          className={`block text-xs font-medium mb-1 ${
            isDark ? "text-gray-300" : "text-gray-600"
          }`}
        >
          {label}
        </label>
      ) : null}
      {children}
    </div>
  );
}
